/**
 * Contextual help, "Ask a question" mode (roadmap lot L3.5) : HTML of the assistant's answer.
 *
 * Pure string building (no DOM) so it is tested by jest. Everything coming from the server
 * (answer text written by a language model, section titles, excerpts) is ESCAPED here : the
 * answer only gets a tiny formatting of its own (paragraphs, "- " lists, **bold**) and its
 * [n] citations become links to the cited section, opened in the panel through the usual
 * [data-help-ref] click handler.
 *
 * Answer shape : api/docAssistant/ask (server/src/api/docAssistant.js)
 *   {mode: "answer"|"sections"|"none", found, answer, citations: [{n, id, title, trail}],
 *    sections: [{id, title, trail, excerpt}], reason}
 *
 * Stable selectors (E2E) : [data-help-answer] on a written answer, [data-help-cite="<n>"] on
 * each citation in the text, [data-help-sources] on the source list, [data-help-ask-sections]
 * on a list of sections without a written answer, [data-help-ask-error] on an error.
 *
 * Feedback (L3.6) : [data-help-feedback] under each answer, [data-help-feedback-up] /
 * [data-help-feedback-down] buttons, [data-help-feedback-form] with the optional comment
 * [data-help-feedback-comment] of a thumbs down, [data-help-feedback-done] once recorded,
 * [data-help-feedback-error] when it could not be.
 *
 * Conversation (2026-10-20 spec "assistant-doc-conversation") : the answers form a thread of
 * exchanges {question, shown, domain, result, feedback}. Stable selectors : [data-help-turn="<i>"]
 * on each exchange (and on its action buttons), [data-help-turn-question] on the user's side,
 * [data-help-domain-banner] above an answer whose domain was assumed, [data-help-clarify] on a
 * clarification with its [data-help-action="ask-domain"][data-help-domain] buttons,
 * [data-help-ask-limit] on the warning / end of the conversation.
 */
const {escapeHtml} = require("./helpRender") ;

const CITATION = /\[(\d+)\]/g ;

/**
 * Inline formatting of one escaped line : **bold** and the [n] citations
 */
function inline(escaped, citations){
    return escaped
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(CITATION, (all, n) => {
            const citation = citations.find((c) => String(c.n) === n) ;
            if(!citation){ return "" ; }
            return '<a class="help-cite" data-help-cite="'+n+'" data-help-ref="'+escapeHtml(citation.id)
                +'" title="'+escapeHtml(citation.title || "")+'">'+n+'</a>' ;
        }) ;
}

/**
 * The answer text as HTML : paragraphs, "- " / "* " / "1. " lines as lists, **bold**, [n] links
 *
 * @param {string} text the answer, plain text
 * @param {object[]} citations [{n, id, title}]
 * @return {string} HTML
 */
function formatAnswer(text, citations){
    const blocks = [] ;
    let list = null ;
    String(text || "").split(/\r?\n/).forEach((raw) => {
        const line = raw.trim() ;
        const item = /^(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line) ;
        if(item){
            if(!list){
                list = {ordered: /^\d/.test(line), items: []} ;
                blocks.push(list) ;
            }
            list.items.push(item[1]) ;
            return ;
        }
        list = null ;
        if(line){ blocks.push(line) ; }
    }) ;
    return blocks.map((b) => {
        if(typeof b === "string"){ return "<p>"+inline(escapeHtml(b), citations)+"</p>" ; }
        const tag = b.ordered ? "ol" : "ul" ;
        return "<"+tag+">"+b.items.map((i) => "<li>"+inline(escapeHtml(i), citations)+"</li>").join("")+"</"+tag+">" ;
    }).join("") ;
}

function trailText(trail){
    return (trail || []).slice(0, -1).join(" › ") ;
}

/**
 * A list of sections, each a link that opens it in the panel
 *
 * @param {object[]} sections [{id, title, trail, excerpt}]
 * @param {object} [opts] {numbered: true for the sources of an answer, excerpts: true to show them}
 */
function sectionsList(sections, opts){
    const o = opts || {} ;
    const tag = o.numbered ? "ol" : "ul" ;
    return "<"+tag+' class="help-results'+(o.numbered ? " help-sources" : "")+'">'+sections.map((s) => {
        const trail = trailText(s.trail) ;
        return '<li><a data-help-ref="'+escapeHtml(s.id)+'">'+escapeHtml(s.title || s.id)+'</a>'
            +(trail ? '<small class="text-muted">'+escapeHtml(trail)+'</small>' : "")
            +(o.excerpts && s.excerpt ? '<small class="help-excerpt">'+escapeHtml(s.excerpt)+'</small>' : "")
            +'</li>' ;
    }).join("")+"</"+tag+">" ;
}

/**
 * HTML of an answer of api/docAssistant/ask
 *
 * @param {object} result the answer
 * @param {function} tr translation function
 * @return {string} HTML
 */
function answerHtml(result, tr){
    const r = result || {} ;
    const sections = Array.isArray(r.sections) ? r.sections : [] ;
    const citations = Array.isArray(r.citations) ? r.citations : [] ;
    if(r.mode === "answer" && r.found && r.answer){
        return '<div class="help-answer" data-help-answer>'+formatAnswer(r.answer, citations)+'</div>'
            +'<p class="help-answer-note"><i class="fas fa-info-circle" aria-hidden="true"></i> '+escapeHtml(tr("help.askAiNote"))+'</p>'
            +(citations.length ? '<div data-help-sources><h2 class="help-block-title">'+escapeHtml(tr("help.askSources"))+'</h2>'
                +sectionsList(citations, {numbered: true})+'</div>' : "") ;
    }
    if(!sections.length){
        return '<p class="help-ask-info" data-help-ask-sections>'+escapeHtml(tr("help.askNothing"))+'</p>' ;
    }
    const intro = r.mode === "answer" ? tr("help.askNoAnswer") : tr("help.askSectionsOnly") ;
    return '<div data-help-ask-sections><p class="help-ask-info">'+escapeHtml(intro)+'</p>'
        +'<h2 class="help-block-title">'+escapeHtml(tr("help.askClosest"))+'</h2>'
        +sectionsList(sections, {excerpts: true})+'</div>' ;
}

/**
 * HTML while the question is processed
 */
function pendingHtml(tr){
    return '<p class="help-ask-info" data-help-ask-pending><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> '
        +escapeHtml(tr("help.askThinking"))+'</p>' ;
}

/**
 * HTML of a failed call : the translated message of the error code when there is one
 *
 * @param {*} err the error of the api call (a code such as DOC_ASSISTANT_DISABLED)
 * @param {function} tr translation function
 */
function errorHtml(err, tr){
    const code = String(err && err.message ? err.message : err || "") ;
    const key = "help.askErrors."+code ;
    const specific = /^[A-Z_]+$/.test(code) ? tr(key) : key ;
    const message = specific && specific !== key ? specific : tr("help.askError") ;
    return '<div class="alert alert-danger help-ask-error" role="alert" data-help-ask-error>'
        +'<i class="fas fa-exclamation-circle" aria-hidden="true"></i> '+escapeHtml(message)
        +' <button type="button" class="btn btn-link btn-sm p-0" data-help-action="ask-retry">'+escapeHtml(tr("help.askRetry"))+'</button></div>' ;
}

const FEEDBACK_COMMENT_MAX = 300 ;

/**
 * HTML of the feedback block under an answer : thumbs up / down, then the optional comment of a
 * thumbs down, then a thank-you. Nothing when the answer cannot be rated (question not recorded).
 *
 * @param {object} result the answer of api/docAssistant/ask ({id, feedbackToken})
 * @param {object} state {open, sending, done, rating, error, draft}
 * @param {function} tr translation function
 * @param {number} [turnIndex] the exchange of the thread it rates (data-help-turn on its buttons)
 * @return {string} HTML
 */
function feedbackHtml(result, state, tr, turnIndex){
    if(!result || !result.id || !result.feedbackToken){ return "" ; }
    const s = state || {} ;
    const turn = turnIndex === undefined || turnIndex === null ? "" : ' data-help-turn="'+escapeHtml(String(turnIndex))+'"' ;
    const commentId = "help-feedback-comment"+(turn ? "-"+escapeHtml(String(turnIndex)) : "") ;
    if(s.done){
        return '<div class="help-feedback" data-help-feedback><p class="help-feedback-done" data-help-feedback-done role="status">'
            +'<i class="fas fa-check text-success" aria-hidden="true"></i> '+escapeHtml(tr("help.feedbackThanks"))+'</p></div>' ;
    }
    const disabled = s.sending ? " disabled" : "" ;
    const pressed = (rating) => (s.open && rating === "down") || s.rating === rating ? "true" : "false" ;
    let html = '<div class="help-feedback" data-help-feedback>'
        +'<div class="help-feedback-ask"><span class="help-feedback-label">'+escapeHtml(tr("help.feedbackQuestion"))+'</span>'
        +'<button type="button" class="btn btn-sm btn-light" data-help-action="feedback-up"'+turn+' data-help-feedback-up aria-pressed="'+pressed("up")+'"'
            +' title="'+escapeHtml(tr("help.feedbackUp"))+'"'+disabled+'><i class="far fa-thumbs-up" aria-hidden="true"></i>'
            +'<span class="sr-only">'+escapeHtml(tr("help.feedbackUp"))+'</span></button>'
        +'<button type="button" class="btn btn-sm btn-light" data-help-action="feedback-down"'+turn+' data-help-feedback-down aria-pressed="'+pressed("down")+'"'
            +' title="'+escapeHtml(tr("help.feedbackDown"))+'"'+disabled+'><i class="far fa-thumbs-down" aria-hidden="true"></i>'
            +'<span class="sr-only">'+escapeHtml(tr("help.feedbackDown"))+'</span></button>'
        +'</div>' ;
    if(s.open){
        html += '<div class="help-feedback-form" data-help-feedback-form>'
            +'<label class="help-ask-label" for="'+commentId+'">'+escapeHtml(tr("help.feedbackCommentLabel"))+'</label>'
            +'<textarea id="'+commentId+'" class="form-control form-control-sm" rows="2" maxlength="'+FEEDBACK_COMMENT_MAX+'"'
                +' data-help-feedback-comment placeholder="'+escapeHtml(tr("help.feedbackCommentPlaceholder"))+'"'+disabled+'>'
                +escapeHtml(s.draft || "")+'</textarea>'
            +'<div class="help-feedback-actions">'
            +'<button type="button" class="btn btn-link btn-sm" data-help-action="feedback-cancel"'+turn+disabled+'>'+escapeHtml(tr("help.feedbackCancel"))+'</button>'
            +'<button type="button" class="btn btn-primary btn-sm" data-help-action="feedback-send"'+turn+' data-help-feedback-send'+disabled+'>'
                +'<i class="fas '+(s.sending ? "fa-spinner fa-spin" : "fa-paper-plane")+'" aria-hidden="true"></i> '+escapeHtml(tr("help.feedbackSend"))+'</button>'
            +'</div></div>' ;
    }
    if(s.error){
        html += '<p class="help-feedback-error text-danger" data-help-feedback-error role="alert">'
            +'<i class="fas fa-exclamation-circle" aria-hidden="true"></i> '+escapeHtml(tr("help.feedbackError"))+'</p>' ;
    }
    return html+'</div>' ;
}

/**
 * "Answer for: <domain>" above an answer whose domain the assistant assumed (from the screen or
 * the documentation cited) : the guarantee that the user knows which domain was answered
 *
 * @param {object} result the answer of api/docAssistant/ask ({domainAssumed, domainLabel})
 * @param {function} tr translation function
 */
function domainBannerHtml(result, tr){
    if(!result || result.domainAssumed !== true || !result.domainLabel){ return "" ; }
    return '<p class="help-domain-banner" data-help-domain-banner><i class="fas fa-filter" aria-hidden="true"></i> '
        +escapeHtml(tr("help.askAnswerFor"))+' <strong>'+escapeHtml(result.domainLabel)+'</strong></p>' ;
}

/**
 * A clarification asked by the assistant : its question and one button per plausible domain,
 * each sending the original question again restricted to that domain
 *
 * @param {object} result the answer of api/docAssistant/ask ({clarify: {question, options: [{domain, label}]}})
 * @param {number} turnIndex the exchange in the thread
 * @param {function} tr translation function
 * @param {object} [opts] {disabled: true when the clarification can no longer be answered}
 */
function clarifyHtml(result, turnIndex, tr, opts){
    const clarify = result && result.clarify ;
    if(!clarify){ return "" ; }
    const options = Array.isArray(clarify.options) ? clarify.options : [] ;
    const disabled = opts && opts.disabled ? " disabled" : "" ;
    const turn = escapeHtml(String(turnIndex)) ;
    return '<div class="help-clarify" data-help-clarify>'
        +'<p class="help-clarify-question">'+escapeHtml(clarify.question || "")+'</p>'
        +(options.length ? '<div class="help-clarify-options">'+options.map((o) => '<button type="button" class="btn btn-outline-primary btn-sm"'
            +' data-help-action="ask-domain" data-help-domain="'+escapeHtml(o.domain)+'" data-help-turn="'+turn+'"'+disabled+'>'
            +escapeHtml(o.label || o.domain)+'</button>').join("")+'</div>' : "")
        +(disabled ? "" : '<small class="form-text text-muted">'+escapeHtml(tr("help.askClarifyHint"))+'</small>')
        +'</div>' ;
}

/**
 * Warning on the last question of a conversation, then its end
 *
 * @param {number} count exchanges done
 * @param {number} maxTurns exchanges allowed in one conversation
 * @param {function} tr translation function
 */
function limitHtml(count, maxTurns, tr){
    if(count >= maxTurns){
        return '<div class="alert alert-warning help-ask-limit" role="status" data-help-ask-limit>'
            +'<i class="fas fa-hourglass-end" aria-hidden="true"></i> '+escapeHtml(tr("help.askLimit"))+'</div>' ;
    }
    if(count === maxTurns - 1){
        return '<p class="help-ask-limit help-ask-info" role="status" data-help-ask-limit>'
            +'<i class="fas fa-info-circle" aria-hidden="true"></i> '+escapeHtml(tr("help.askLastTurn"))+'</p>' ;
    }
    return "" ;
}

/**
 * The conversation : each exchange (the user's question, then the answer, the clarification,
 * the spinner of the pending one or the error of the failed one), then the limit message
 *
 * @param {object[]} turns [{question, shown, domain, result, feedback}] (result null while pending / failed)
 * @param {object} state {pending, error, maxTurns}
 * @param {function} tr translation function
 * @return {string} HTML
 */
function threadHtml(turns, state, tr){
    const list = Array.isArray(turns) ? turns : [] ;
    const s = state || {} ;
    const done = list.filter((t) => t.result).length ;
    const blocked = s.pending || done >= s.maxTurns ;
    const html = list.map((t, i) => {
        const last = i === list.length - 1 ;
        let answer = "" ;
        if(t.result){
            const r = t.result ;
            answer = domainBannerHtml(r, tr)
                +(r.mode === "clarify" && r.clarify ? clarifyHtml(r, i, tr, {disabled: !last || blocked}) : answerHtml(r, tr))
                +(r.mode === "answer" ? feedbackHtml(r, t.feedback, tr, i) : "") ;
        }else if(last && s.pending){
            answer = pendingHtml(tr) ;
        }else if(last && s.error){
            answer = errorHtml(s.error, tr) ;
        }
        return '<div class="help-turn" data-help-turn="'+i+'">'
            +'<p class="help-turn-question" data-help-turn-question>'+escapeHtml(t.shown || t.question || "")+'</p>'
            +(answer ? '<div class="help-turn-answer">'+answer+'</div>' : "")
            +'</div>' ;
    }).join("") ;
    return html+(s.pending ? "" : limitHtml(done, s.maxTurns, tr)) ;
}

module.exports = {
    FEEDBACK_COMMENT_MAX,
    threadHtml,
    domainBannerHtml,
    clarifyHtml,
    limitHtml,
    feedbackHtml,
    formatAnswer,
    answerHtml,
    pendingHtml,
    errorHtml,
} ;
